import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { Toaster } from "./components/ui/sonner";
import InstallPWA from "./components/InstallPWA";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import TeachersPage from "./pages/TeachersPage";
import KathaPage from "./pages/KathaPage";
import ShopPage from "./pages/ShopPage";
import EventsPage from "./pages/EventsPage";
import LiveKirtanPage from "./pages/LiveKirtanPage";
import CoursesPage from "./pages/CoursesPage";
import ChatbotPage from "./pages/ChatbotPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import OrdersPage from "./pages/OrdersPage";
import CollaboratePage from "./pages/CollaboratePage";
import LocalDirectoryPage from "./pages/LocalDirectoryPage";
import AdvertisePage from "./pages/AdvertisePage";
import AdvertiseSuccessPage from "./pages/AdvertiseSuccessPage";
import DonatePage from "./pages/DonatePage";
import DonateSuccessPage from "./pages/DonateSuccessPage";
import AboutPage from "./pages/AboutPage";
import SettingsPage from "./pages/SettingsPage";
import VideoCallPage from "./pages/VideoCallPage";

// Admin Pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminTeachers from "./pages/admin/AdminTeachers";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminLiveStreams from "./pages/admin/AdminLiveStreams";
import AdminKatha from "./pages/admin/AdminKatha";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminRequests from "./pages/admin/AdminRequests";
import AdminAdvertisements from "./pages/admin/AdminAdvertisements";
import AdminMessages from "./pages/admin/AdminMessages";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminPaymentSettings from "./pages/admin/AdminPaymentSettings";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-100">
        <div className="w-16 h-16 border-4 border-saffron-200 border-t-saffron-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Layout wrapper
const Layout = ({ children, showFooter = true }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      {showFooter && <Footer />}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/login" element={<Layout showFooter={false}><LoginPage /></Layout>} />
            <Route path="/register" element={<Layout showFooter={false}><RegisterPage /></Layout>} />
            <Route path="/teachers" element={<Layout><TeachersPage /></Layout>} />
            <Route path="/katha" element={<Layout><KathaPage /></Layout>} />
            <Route path="/shop" element={<Layout><ShopPage /></Layout>} />
            <Route path="/events" element={<Layout><EventsPage /></Layout>} />
            <Route path="/live-kirtan" element={<Layout showFooter={false}><LiveKirtanPage /></Layout>} />
            <Route path="/courses" element={<Layout><CoursesPage /></Layout>} />
            <Route path="/chatbot" element={<Layout showFooter={false}><ChatbotPage /></Layout>} />
            <Route path="/collaborate" element={<Layout><CollaboratePage /></Layout>} />
            <Route path="/directory" element={<Layout><LocalDirectoryPage /></Layout>} />
            <Route path="/advertise" element={<Layout><AdvertisePage /></Layout>} />
            <Route path="/advertise/success" element={<Layout><AdvertiseSuccessPage /></Layout>} />
            <Route path="/donate" element={<Layout><DonatePage /></Layout>} />
            <Route path="/donate/success" element={<Layout><DonateSuccessPage /></Layout>} />
            <Route path="/about" element={<Layout><AboutPage /></Layout>} />
            <Route path="/settings" element={<Layout><ProtectedRoute><SettingsPage /></ProtectedRoute></Layout>} />
            <Route path="/video-call" element={<Layout showFooter={false}><ProtectedRoute><VideoCallPage /></ProtectedRoute></Layout>} />
            <Route path="/video-call/:roomId" element={<Layout showFooter={false}><ProtectedRoute><VideoCallPage /></ProtectedRoute></Layout>} />
            
            {/* Protected routes */}
            <Route path="/cart" element={<Layout><ProtectedRoute><CartPage /></ProtectedRoute></Layout>} />
            <Route path="/checkout" element={<Layout showFooter={false}><ProtectedRoute><CheckoutPage /></ProtectedRoute></Layout>} />
            <Route path="/checkout/success" element={<Layout><ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute></Layout>} />
            <Route path="/orders" element={<Layout><ProtectedRoute><OrdersPage /></ProtectedRoute></Layout>} />
            
            {/* Admin routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="teachers" element={<AdminTeachers />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="live-streams" element={<AdminLiveStreams />} />
              <Route path="katha" element={<AdminKatha />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="requests" element={<AdminRequests />} />
              <Route path="advertisements" element={<AdminAdvertisements />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="payment-settings" element={<AdminPaymentSettings />} />
            </Route>
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
        <InstallPWA />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
